import React from 'react';
import { IdPs, requiredLogoutUrlParams } from '../constants/providers';
import { AppConfig } from '../types/app';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-react';
import logoutMessages from '../i18n/logout.messages';
import providersService from '../services/providers';
import globalMessages from '../i18n/global.messages';

const LogoutInstructions = ({
  config,
  idp,
}: {
  config: AppConfig;
  idp?: IdPs;
}): JSX.Element => {
  const { formatMessage } = useIntl();

  const clientID = providersService.getClientId(requiredLogoutUrlParams);
  const clientFromConfig = config.clients[clientID];
  const lxpName =
    clientFromConfig?.displayName ||
    formatMessage(logoutMessages.fallbackLxpName);

  const enableHomePageButtonInLogOutScreen =
    clientFromConfig?.enableHomePageButtonInLogOutScreen === 'true';

  const homePageUrl = clientFromConfig?.homePageURL;

  const enableBuilderIdShimLogout = config.enableBuilderIdShimLogout === 'true';

  const globalLxpName = formatMessage(globalMessages.pageLogoAlt);
  const partnerCentralUrl = 'https://partnercentral.awspartner.com/';
  const nextGenMidwayFaqUrl =
    'https://w.amazon.com/bin/view/NextGenMidway/FAQ#How_do_I_logout_of_NextGen_Midway_so_I_can_test_login_.2F_reset_my_timeout_to_20_hours_before_starting_work_I_don.27t_want_interrupted.3F';
  const profileAwsUrl = 'https://profile.aws.amazon.com';
  const signOutAllBidUrl =
    'https://docs.aws.amazon.com/signin/latest/userguide/how-to-sign-out-in.html#sign-out-all-aws_builder_id';

  const formatHrefBlankTargetNoReferrer = (href: string) => {
    return (str) => (
      <a href={href} target="_blank" rel="noreferrer">
        {str}
      </a>
    );
  };

  const standardMessage = (logoutMessage: any, lxpName: string) => {
    return formatMessage(logoutMessage, {
      lxp: lxpName,
    });
  };

  const standardMessageWithHref = (
    logoutMessage: any,
    lxpName: string,
    href: string
  ) => {
    return formatMessage(logoutMessage, {
      lxp: lxpName,
      link: formatHrefBlankTargetNoReferrer(href),
    });
  };

  const bidStandardMessage = (logoutMessage: any, lxpName: string) => {
    return formatMessage(logoutMessage, {
      link: formatHrefBlankTargetNoReferrer(profileAwsUrl),
      instructions: formatHrefBlankTargetNoReferrer(signOutAllBidUrl),
      br: <br />,
      lxp: lxpName,
    });
  };

  const genericEnterpriseInstructions = (
    <TextContent>
      {standardMessage(
        logoutMessages.organizationESSOSignOutInstructionsGlobal,
        globalLxpName
      )}
    </TextContent>
  );

  const idpLogoutInfo: {
    [i in IdPs]:
      | React.ReactNode
      | { displayName: string; instructions: JSX.Element };
  } = {
    [IdPs.OTP]: (
      <TextContent>
        {standardMessage(
          logoutMessages.emailOtpSignOutInstructionsGlobal,
          globalLxpName
        )}
      </TextContent>
    ),
    [IdPs.AWSBuilderID]: (
      <>
        <TextContent>
          {enableBuilderIdShimLogout
            ? standardMessage(
                logoutMessages.bidShimSignOutInstructions,
                globalLxpName
              )
            : bidStandardMessage(
                logoutMessages.bidSignOutInstructionsGlobal,
                globalLxpName
              )}
        </TextContent>
      </>
    ),
    [IdPs.LoginWithAmazon]: (
      <>
        <ul>
          <li>
            {' '}
            <TextContent>
              {formatMessage(logoutMessages.lwaSignOutInstructionsOne)}
            </TextContent>
          </li>
          <li>
            {' '}
            <TextContent>
              {formatMessage(logoutMessages.lwaSignOutInstructionsTwo)}
            </TextContent>
          </li>
          <li>
            {' '}
            <TextContent>
              {formatMessage(logoutMessages.lwaSignOutInstructionsThree)}
            </TextContent>
          </li>
        </ul>
        <TextContent>
          {formatMessage(logoutMessages.forMoreInformation, {
            link: formatHrefBlankTargetNoReferrer(
              'https://www.amazon.com/gp/help/customer/display.html?nodeId=GQL359FK7DM8294C'
            ),
          })}
        </TextContent>
      </>
    ),
    [IdPs.AWSSignIn]: <></>,
    [IdPs.AmazonFederate]: (
      <>
        <TextContent>
          {standardMessageWithHref(
            logoutMessages.amazonFederateSignOutInstructionsGlobal,
            globalLxpName,
            nextGenMidwayFaqUrl
          )}
        </TextContent>
      </>
    ),
    [IdPs.AmazonSystemFederate]: <></>,
    [IdPs.ADFS]: {
      displayName: 'ADFS',
      instructions: genericEnterpriseInstructions,
    },
    [IdPs.JPMC]: {
      displayName: 'JPMC',
      instructions: genericEnterpriseInstructions,
    },
    [IdPs.ScaleTestIdp]: { displayName: 'Scale Test IDP', instructions: <></> },
    [IdPs.AWSPartnerNetwork]: (
      <>
        <TextContent>
          {standardMessageWithHref(
            logoutMessages.awsPartnerNetworkSignOutInstructionsGlobal,
            globalLxpName,
            partnerCentralUrl
          )}
        </TextContent>
      </>
    ),
    [IdPs.Accenture]: genericEnterpriseInstructions,
    [IdPs.AccentureUAT]: genericEnterpriseInstructions,
    [IdPs.testProvider]: <></>,
    [IdPs.BestBuy]: genericEnterpriseInstructions,
    [IdPs.Westpac]: genericEnterpriseInstructions,
    [IdPs.Sophos]: genericEnterpriseInstructions,
    [IdPs.Shell]: genericEnterpriseInstructions,
    [IdPs.Jefferies]: genericEnterpriseInstructions,
    [IdPs.Curtin]: genericEnterpriseInstructions,
    [IdPs.Principal]: genericEnterpriseInstructions,
    [IdPs.GandalfSession]: <></>,
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <>
        {idp && idpLogoutInfo[idp]
          ? idpLogoutInfo[idp]
          : genericEnterpriseInstructions}
      </>

      {enableHomePageButtonInLogOutScreen && homePageUrl && (
        <Box textAlign="center">
          <Button
            variant="primary"
            href={homePageUrl}
            data-testid="return-to-homepage-button"
          >
            {formatMessage(logoutMessages.returnToHomepage, { lxp: lxpName })}
          </Button>
        </Box>
      )}
    </SpaceBetween>
  );
};

export default LogoutInstructions;
